/* ── Overlay ──────────────────────────────────────────────── */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(4, 13, 24, 0.88);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-4);
  z-index: 300;
}

.modal {
  width: 100%;
  max-width: 480px;
  background: var(--color-surface);
  border: 1px solid var(--color-border-med);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-strong);
  position: relative;
  overflow: hidden;
}

.closeBtn {
  position: absolute;
  top: var(--sp-4);
  right: var(--sp-4);
  background: none;
  border: none;
  color: var(--color-text-muted);
  font-size: 16px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: var(--radius-sm);
  line-height: 1;
  z-index: 1;
  transition: color 0.15s, background 0.15s;
}

.closeBtn:hover { color: var(--color-text); background: rgba(255,255,255,0.05); }

/* ── Pane (shared wrapper) ───────────────────────────────── */
.pane {
  padding: var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

/* ── Provider hero ───────────────────────────────────────── */
.providerHero {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  padding-right: var(--sp-8);
}

.providerIcon {
  font-size: 28px;
  color: var(--color-sky-dim);
  flex-shrink: 0;
  line-height: 1;
}

.providerName {
  font-family: var(--font-mono);
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text);
}

.providerCat {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: 2px;
}

/* ── Sync note ───────────────────────────────────────────── */
.syncNote {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--sp-3) var(--sp-4);
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.syncLabel {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--color-text-faint);
}

.syncText {
  font-size: 12px;
  color: var(--color-text-muted);
  line-height: 1.5;
}

.setupStepsList {
  margin: var(--sp-2) 0 0;
  padding-left: 18px;
  font-size: 12px;
  color: var(--color-text-muted);
  line-height: 1.6;
}

.setupStepsList li {
  margin-bottom: var(--sp-2);
}

.setupStepsList li:last-child {
  margin-bottom: 0;
}

/* ── OAuth pane ──────────────────────────────────────────── */
.oauthHint {
  font-size: 13px;
  color: var(--color-text-muted);
  line-height: 1.6;
}

.authorizeBtn {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 600;
  padding: var(--sp-3) var(--sp-5);
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, rgba(56,189,248,0.15) 0%, rgba(13,148,136,0.15) 100%);
  border: 1px solid rgba(56, 189, 248, 0.3);
  color: var(--color-sky);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  width: 100%;
}

.authorizeBtn:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(56,189,248,0.22) 0%, rgba(13,148,136,0.22) 100%);
  border-color: rgba(56, 189, 248, 0.5);
}

.authorizeBtn:disabled { opacity: 0.4; cursor: default; }

.devNote {
  font-size: 11px;
  color: var(--color-text-faint);
  text-align: center;
}

/* ── Status boxes ────────────────────────────────────────── */
.statusBox {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-5);
  border-radius: var(--radius-md);
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  text-align: center;
  font-size: 13px;
  color: var(--color-text-muted);
}

.statusSuccess {
  background: rgba(34, 197, 94, 0.07);
  border-color: rgba(34, 197, 94, 0.2);
  color: var(--color-success);
}

.statusError {
  background: rgba(239, 68, 68, 0.07);
  border-color: rgba(239, 68, 68, 0.2);
  color: var(--color-error);
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--color-border-med);
  border-top-color: var(--color-sky);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.closeAfterBtn,
.retryBtn {
  font-family: var(--font-mono);
  font-size: 12px;
  padding: var(--sp-2) var(--sp-4);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.15s;
}

.closeAfterBtn {
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.25);
  color: var(--color-success);
}

.closeAfterBtn:hover { background: rgba(34, 197, 94, 0.2); }

.retryBtn {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.25);
  color: var(--color-error);
}

.retryBtn:hover { background: rgba(239, 68, 68, 0.18); }

/* ── REST pane steps ─────────────────────────────────────── */
.steps {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
}

.step {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex: 1;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.step + .step::before {
  content: '';
  display: block;
  width: 20px;
  height: 1px;
  background: var(--color-border-med);
  flex-shrink: 0;
  margin-right: var(--sp-2);
}

.stepActive { opacity: 1; }
.stepDone   { opacity: 0.7; }

.stepNum {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border-med);
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.stepActive .stepNum {
  background: rgba(56, 189, 248, 0.12);
  border-color: rgba(56, 189, 248, 0.4);
  color: var(--color-sky);
}

.stepDone .stepNum {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.3);
  color: var(--color-success);
}

.stepLabel {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: var(--color-text-muted);
  white-space: nowrap;
}

.stepActive .stepLabel { color: var(--color-text); }

/* ── Step body ───────────────────────────────────────────── */
.stepBody {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.fieldLabel {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.optionalTag {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: var(--color-text-faint);
}

.fieldInput {
  width: 100%;
  padding: var(--sp-2) var(--sp-3);
  background: var(--color-surface-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
}

.fieldInput::placeholder { color: var(--color-text-faint); }
.fieldInput:focus { border-color: var(--color-border-med); }

.fieldInputTop { margin-top: var(--sp-2); }

.fieldHint {
  font-size: 11px;
  color: var(--color-text-faint);
  line-height: 1.5;
}

.fieldError {
  font-size: 11px;
  color: var(--color-error, #f87171);
  line-height: 1.5;
}

/* ── Radio group ─────────────────────────────────────────── */
.radioGroup {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.radioLabel {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  font-size: 13px;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  transition: border-color 0.15s, background 0.15s;
}

.radioLabel:has(input:checked) {
  border-color: rgba(56, 189, 248, 0.35);
  background: rgba(56, 189, 248, 0.05);
  color: var(--color-text);
}

.radioLabel:hover { border-color: var(--color-border-med); }

/* ── Step actions ────────────────────────────────────────── */
.stepActions {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
  margin-top: var(--sp-2);
}

.backBtn {
  font-family: var(--font-mono);
  font-size: 12px;
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--radius-sm);
  background: transparent;
  border: 1px solid var(--color-border);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.backBtn:hover { border-color: var(--color-border-med); color: var(--color-text); }

.nextBtn {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  padding: var(--sp-2) var(--sp-4);
  border-radius: var(--radius-sm);
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.25);
  color: var(--color-sky);
  cursor: pointer;
  transition: background 0.15s;
}

.nextBtn:hover:not(:disabled) { background: rgba(56, 189, 248, 0.18); }
.nextBtn:disabled { opacity: 0.4; cursor: default; }

.testBtn {
  font-family: var(--font-mono);
  font-size: 12px;
  padding: var(--sp-2) var(--sp-4);
  border-radius: var(--radius-sm);
  background: transparent;
  border: 1px solid var(--color-border-med);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.testBtn:hover:not(:disabled) { border-color: var(--color-sky-dim); color: var(--color-text); }
.testBtn:disabled { opacity: 0.4; cursor: default; }

/* ── Database schema checklist ──────────────────────────────── */
.tableListHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tableListSummary {
  font-size: 11px;
  color: var(--color-text-faint);
}

.tableListActions {
  display: flex;
  gap: var(--sp-2);
}

.tableListActionBtn {
  background: none;
  border: none;
  color: var(--color-sky);
  font-size: 11px;
  font-family: var(--font-mono);
  cursor: pointer;
  padding: 0;
}

.tableListActionBtn:hover { color: var(--color-sky-dim); }

.tableList {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  max-height: 280px;
  overflow-y: auto;
  padding-right: var(--sp-1);
}

.tableRow {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.tableRow:has(input:checked) {
  border-color: rgba(56, 189, 248, 0.35);
  background: rgba(56, 189, 248, 0.05);
}

.tableRow:hover { border-color: var(--color-border-med); }

.tableRowCheckbox { margin-top: 2px; }

.tableRowBody {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.tableRowTop {
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
}

.tableRowName {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text);
}

.tableRowCount {
  font-size: 10px;
  color: var(--color-text-faint);
}

.tableRowReason {
  font-size: 11px;
  color: var(--color-text-muted);
  line-height: 1.4;
}

/* ── Chat escape hatch ───────────────────────────────────── */
.chatLink {
  background: none;
  border: none;
  color: var(--color-text-faint);
  font-size: 12px;
  font-family: var(--font-sans);
  cursor: pointer;
  padding: 0 0 var(--sp-2);
  text-align: center;
  transition: color 0.15s;
  margin-top: var(--sp-2);
}

.chatLink:hover { color: var(--color-sky); }
